import React from 'react';
import { nucleusConstants } from '@lifesize/nucleus';
import { Field, Form, Formik } from 'formik';
import intl from 'react-intl-universal';
import classnames from 'classnames';
import _get from 'lodash/get';
import _memoize from 'lodash/memoize';
import { Mutation } from 'react-apollo';
import * as Yup from 'yup';
import ResetPassword from 'containers/ResetPassword/ResetPasswordContainer';
import { Logger } from '../../../../logger';
import { User } from 'typings/types';
import { getUserType, isAdminUser, isSameUser, isSuperUser } from 'utils/userProfileUtils';
import Icon from 'components/Common/Icon';
import { ModalFooter } from 'components/Modal/ModalComponent/ModalFooter';
import ToggleButton from 'components/ToggleButton/ToggleButton';
import { Tooltip } from 'components/tooltip/Tooltip';
import * as formStyles from 'components/Modals/ModalForms.scss';
import updateUserMutation from 'queries/users/updateUserMutation.gql';
import { directoryHidingOverride } from 'constants/localStorageConsts';

interface Props {
  closeModal: () => void;
  currentUser: object;
  hasDirectoryHidingFeature: boolean;
  selectedUser: User;
  resetPasswordHandler: (uuid: string) => void;
}

const getHideUserLabel = (labelText: string, toolTipText: string) => {
  return (
    <>
      <span>{labelText}</span>
      <Tooltip text={toolTipText} stylesWrapper={{ lineHeight: 1, marginLeft: '8px' }}>
        <Icon classes={['icon-info', formStyles.tooltipButtonIcon]} />
      </Tooltip>
    </>
  );
};

class InfoTab extends React.Component<Props> {

  /**
   * Cached to evaluate only once
   */
  infoTabSchema = _memoize(() => {
    return Yup.object().shape({
      displayName: Yup.string().trim().required(intl.get('userNameRequired'))
        .max(32, intl.get('userNameMaxLength')),
    });
  });

  canEditName = (selectedUser: User, currentUser: object) => {
    const isEditingMe = isSameUser(selectedUser, currentUser);
    if (isAdminUser(currentUser)) {
      // Admin should not be allowed to change their personal name from user settings modal.
      return !isEditingMe;
    }
    if (isSuperUser(currentUser)) {
      // If the selected item is admin user, or themselves, they cannot change
      if (isAdminUser(selectedUser) || isEditingMe) {
        return false;
      }
      // Otherwise they can reset anyone
      return true;
    }
    // Other users can change if it's their account
    return isEditingMe;
  }

  canHideUser = (selectedUser: User, currentUser: object) => {
    return isAdminUser(currentUser) || (isSuperUser(currentUser) && !isAdminUser(selectedUser));
  }

  canSetAdminAccess = (selectedUser: User, currentUser: object) => {
    const isEditingMe = isSameUser(selectedUser, currentUser);
    if (isAdminUser(currentUser)) {
      // Admin should not be allowed to change their personal admin access from user settings modal.
      return !isEditingMe;
    }
    if (isSuperUser(currentUser)) {
      // If the selected item is admin user, or themselves, they cannot change
      if (isAdminUser(selectedUser) || isEditingMe) {
        return false;
      }
      // Otherwise they can reset anyone
      return true;
    }
    // Other users can't change this
    return false;
  }

  canResetPassword = (selectedUser: User, currentUser: object) => {
    const isEditingMe = isSameUser(selectedUser, currentUser);
    if (isAdminUser(currentUser)) {
      // Admin should not be allowed to reset their password from user settings modal.
      // They should use My Profile
      return !isEditingMe;
    }
    if (isSuperUser(currentUser)) {
      // If the selected item is admin user, or themselves, they cannot change
      if (isAdminUser(selectedUser) || isEditingMe) {
        return false;
      }
      // Otherwise they can reset anyone
      return true;
    }
    // Regular user cannot change password, unless it's their personal account
    return isEditingMe;
  }

  render(): React.ReactNode {
    const {
      closeModal,
      currentUser,
      hasDirectoryHidingFeature,
      selectedUser,
    } = this.props;

    const userType = getUserType(selectedUser);
    const canEditName = this.canEditName(selectedUser, currentUser);
    const canHideUser = this.canHideUser(selectedUser, currentUser);
    const canSetAdminAccess = this.canSetAdminAccess(selectedUser, currentUser);
    const canResetPassword = this.canResetPassword(selectedUser, currentUser);
    // User can't do anything if they can't edit any form fields
    const isReadOnly = (!canEditName && !canSetAdminAccess && !canHideUser);

    return (
      <Mutation mutation={updateUserMutation}>
        {(updateUser) => (
          <Formik
            initialValues={{
              displayName: _get(selectedUser, 'displayName', ''),
              hidden: _get(selectedUser, 'hidden', false),
              userType: userType === nucleusConstants.USER_ROLE_SUPER_USER
            }}
            validationSchema={this.infoTabSchema}
            onSubmit={async (values, { setSubmitting, setFieldError }) => {
              let inputUserType = values.userType ? nucleusConstants.USER_ROLE_SUPER_USER : nucleusConstants.USER_ROLE_USER;
              if (isAdminUser(selectedUser)) { // You cannot change admin user roles
                inputUserType = nucleusConstants.USER_ROLE_ADMIN;
              }
              try {
                await updateUser({
                  variables: {
                    user: {
                      uuid: selectedUser.UUID,
                      displayName: values.displayName,
                      hidden: values.hidden,
                      userType: inputUserType
                    }
                  }
                });
                closeModal();
              } catch (e) {
                if (e.message && e.message.toLowerCase().trim() === 'graphql error: displaynameexisterror') {
                  setFieldError('displayName', intl.get('duplicateUserNameError', { value: values.displayName }));
                }
                Logger.info(`Failure to save user: ${e} `);
                setSubmitting(false);
              }
            }}
            render={({ isSubmitting, isValid, errors, touched, dirty }) => (
              <Form>
                <div className={classnames(formStyles.formGroup, canEditName && formStyles.hasValidation)}>
                  <label className={classnames(formStyles.labelLarge, formStyles.favorField)}>
                    {intl.get('userNameLabel')}
                  </label>
                  {canEditName ? (
                    <div className={classnames(formStyles.inputContainer, formStyles.favorField)}>
                      <Field
                        className={formStyles.input}
                        type="text"
                        name="displayName"
                        maxLength={32}
                      />
                      <ul className={formStyles.inputMessages}>
                        {touched && errors && touched.displayName && errors.displayName ? (
                          <li className={formStyles.error}>{errors.displayName}</li>
                        ) : null}
                      </ul>
                    </div>
                  ) : (
                      <div className={classnames(formStyles.inputContainer, formStyles.settingValue)}>
                        {_get(selectedUser, 'displayName', '')}
                      </div>
                    )}
                </div>
                {
                  canSetAdminAccess &&
                  <div className={formStyles.formGroup}>
                    <Field
                      classNameLabel={formStyles.toggleLabelLarge}
                      component={ToggleButton}
                      name="userType"
                      id="isAdmin"
                      label={intl.get('userIsAdminLabel')}
                      leftLabel={true}
                    />
                  </div>
                }

                {(directoryHidingOverride || (canHideUser && hasDirectoryHidingFeature)) &&
                  <div className={formStyles.formGroup}>
                    <Field
                      classNameLabel={formStyles.toggleLabelLarge}
                      component={ToggleButton}
                      id="hidden"
                      label={getHideUserLabel(intl.get('inputLabelHideUser'), intl.get('tooltipHideUser'))}
                      leftLabel={true}
                      name="hidden"
                    />
                  </div>
                }
                {
                  canResetPassword &&
                  <div className={classnames(formStyles.formGroup, formStyles.vertical)}>
                    <div className={classnames(formStyles.inputContainer, formStyles.center)}>
                      <ResetPassword onClickHandler={() => this.props.resetPasswordHandler(_get(selectedUser, 'UUID'))} />
                    </div>
                  </div>
                }
                <ModalFooter
                  useSaveAsFormSubmit={true}
                  closeText={intl.get('modalCancel')}
                  closeModal={closeModal}
                  isBusy={isSubmitting}
                  hideSaveButton={isReadOnly}
                  isSaveButtonDisabled={!dirty || isSubmitting || !isValid}
                />
              </Form>
            )}
          />
        )}
      </Mutation>
    );
  }
}

export default InfoTab;
